import {
  BaseResponseSchema,
  EmailTextAreaTypeSchema,
  getRequiredMessage,
} from '@monetize/types/common';
import { object, string, z } from 'zod';
import { AddressSchema } from './address.types';
import { ContactRespSchema } from './contact.types';
import { CustomFieldRecordSchema } from './customFields.types';

export enum AccountTypeEnum {
  CUSTOMER = 'CUSTOMER',
  PARTNER = 'PARTNER',
}
export const AccountTypeEnumZ = z.enum(AccountTypeEnum);

export const AccountTypeDisplayText: {
  [key in AccountTypeEnum]: string;
} = {
  CUSTOMER: 'Customer',
  PARTNER: 'Partner',
};

export enum AccountStatusEnum {
  ACTIVE = 'ACTIVE',
  CANCELED = 'CANCELED',
  SUSPENDED = 'SUSPENDED',
  INACTIVE = 'INACTIVE',
  ARCHIVED = 'ARCHIVED',
}
export const AccountStatusEnumZ = z.enum(AccountStatusEnum);

export const AccountSchema = object({
  customId: z.string().max(50).nullish(),
  status: AccountStatusEnumZ,
  isTestAccount: z.boolean(),
  // locale: z.string().max(15).nonempty("Locale can't be empty."),
  accountName: z
    .string()
    .min(1, getRequiredMessage('Account Name'))
    .max(255, 'Account Name must not exceed 255 characters.'),
  defaultLegalEntityId: z.string(),
  defaultCurrency: z.string(),
  defaultDunningProcessId: z.string().nullish(),
  taxEntityUseCode: z.string().nullish(),
  taxExemptionNumber: z.string().nullish(),
  customFields: CustomFieldRecordSchema,
  shippingAddress: AddressSchema.nullish(),
  billingAddress: AddressSchema.nullish(),
  ccEmails: EmailTextAreaTypeSchema,
  bccEmails: EmailTextAreaTypeSchema,
  accountType: AccountTypeEnumZ.default(AccountTypeEnum.CUSTOMER),
});

export type IAccountSchema = z.infer<typeof AccountSchema>;

/**
 * UI-specific types for form handling
 *
 * We use separate input and output types because Zod's .default() modifier creates a difference:
 * - Input type: What the form accepts (accountType is OPTIONAL because .default() will fill it)
 * - Output type: What Zod validates to (accountType becomes REQUIRED after .default() is applied)
 *
 * Use these types when working with forms:
 * - AccountFormInput: For useForm<T> generic and form state
 * - AccountFormOutput: For onSubmit(data: T) parameter after validation
 */
export type AccountFormInput = z.input<typeof AccountSchema>;
export type AccountFormOutput = z.output<typeof AccountSchema>;

export const AccountSummarySchema = object({
  id: z.string(),
  customId: z.string().nullish(),
  isTestAccount: z.boolean(),
  accountName: z.string(),
  defaultCurrency: z.string(),
  status: AccountStatusEnumZ,
  defaultLegalEntityId: z.string(),
});

export type AccountSummary = z.infer<typeof AccountSummarySchema>;

// there's a good chance these 2 enums are actually shared across schemas and can then be moved miscTypes file:
export enum AccountHistoryObjectTypeEnum {
  ACCOUNT = 'ACCOUNT',
  BILL_GROUP = 'BILL_GROUP',
  CONTACT = 'CONTACT',
  TEAM = 'TEAM',
  SUBSCRIPTION = 'SUBSCRIPTION',
}
export enum AccountHistoryOperationEnum {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
}

export interface IAccountHistoryResp {
  id: string;
  createDate: string;
  createdBy: string;
  message: string;
  objectId: string;
  operation: AccountHistoryOperationEnum;
  objectType: AccountHistoryObjectTypeEnum;
}

export const AccountRespSchema = BaseResponseSchema.extend({
  customId: string().nullish(),
  isTestAccount: z.boolean(),
  locale: string().nullish(),
  accountName: string(),
  /** @deprecated */
  currency: string().nullish(),
  defaultCurrency: z.string(),
  defaultDunningProcessId: z.string().nullish(),
  defaultLegalEntityId: z.string(),
  parentId: z.string().nullish(),
  rootAccountId: z.string().nullish(),
  accountType: AccountTypeEnumZ.default(AccountTypeEnum.CUSTOMER),

  status: AccountStatusEnumZ,
  primaryContact: ContactRespSchema.nullish(),

  taxEntityUseCode: z.string().nullish(),
  taxExemptionNumber: z.string().nullish(),

  customFields: CustomFieldRecordSchema,

  shippingAddress: AddressSchema.nullish(),
  billingAddress: AddressSchema.nullish(),
  noteCount: z.number(),
  ccEmails: EmailTextAreaTypeSchema,
  bccEmails: EmailTextAreaTypeSchema,

  pastDueInvoiceCount: z.number().nullish(),
  minimumInvoiceDueDate: string().nullish(),
  pastDueAmountOutstanding: z.number().nullish(),
  amountOutstanding: z.number().nullish(),
});

export type IAccountRespSchema = z.infer<typeof AccountRespSchema>;

export enum AccountHierarchyResponseTypeEnum {
  FLAT = 'FLAT',
  NESTED = 'NESTED',
}

export const AccountHierarchyNodeSchema = object({
  id: z.string(),
  name: z.string(),
  parentId: z.string().nullish(),
});

export type AccountHierarchyNode = z.infer<typeof AccountHierarchyNodeSchema>;

export const AccountHierarchyFlatSchema = object({
  requestedAccountId: z.string(),
  rootAccountId: z.string(),
  responseType: z.literal(AccountHierarchyResponseTypeEnum.FLAT),
  accounts: z.array(AccountHierarchyNodeSchema),
});

export type AccountHierarchyFlat = z.infer<typeof AccountHierarchyFlatSchema>;

export const AccountHierarchyNestedSchema = object({
  id: z.string(),
  name: z.string(),
  responseType: z.literal(AccountHierarchyResponseTypeEnum.NESTED),
  get children() {
    return z.array(AccountHierarchyNestedSchema.omit({ responseType: true }));
  },
});

export type AccountHierarchyNested = z.infer<
  typeof AccountHierarchyNestedSchema
>;

export interface AccountHierarchyListItem {
  id: string;
  name: string;
  parentId: string | null | undefined;
  level: number;
}
